/*
 * <sat-photo> — the photography frame.
 *
 * The design component used Claude Design's <image-slot> placeholder element, which is
 * canvas-editor tooling and does not belong in a storefront. CLAUDE.md names photography
 * as the one real gap in the design ("Photography is the one real gap … Don't draw
 * illustrations in SVG to fill them"), so this element does exactly two things:
 *
 *   1. If a photograph is available it renders it, cover-cropped, filling the frame.
 *      Sources, in order: the `src` attribute (bound to a real Shopify product image by
 *      the app), then window.SAT_PHOTOS[slot-id] (emitted by snippets/sat-bootstrap.liquid
 *      from product handles and theme settings). An element carrying `fit="contain"`
 *      opts out of the crop — the whole photograph is shown, letterboxed instead of cut
 *      into, for the spots (the Import's rear cover, its ad-grid photos, the Manifest
 *      grid, the product detail modal) where showing the whole item matters more than
 *      filling the frame edge-to-edge; a fixed box just letterboxes around whatever
 *      doesn't fill it rather than cutting the item off.
 *
 *      A `fit="contain"` element can ALSO carry `auto-ratio`: once the real photograph
 *      loads, the host element sets its own CSS aspect-ratio to the image's true
 *      natural aspect ratio (clamped to a sane range so one extreme product photo can't
 *      produce an absurdly tall or wide box) instead of sitting inside whatever fixed
 *      aspect-ratio the surrounding markup gave it. Letterboxing with a fixed ratio
 *      already stops the crop, but it still wastes visible space around a photo whose
 *      real shape doesn't match that box — auto-ratio reframes the box to the photo
 *      instead, so contain has nothing left to pad. Meant for single, standalone photo
 *      slots (the product detail modal) where the container is free to take whatever
 *      height the image implies; not used in fixed multi-column grids, where every
 *      card needs to agree on a shape.
 *   2. If none is available it holds the exact frame the layout expects and states, in
 *      the house voice, that the photograph has not been taken yet. It never invents art.
 *      The one exception is an element carrying the `atmosphere` attribute: those are
 *      decorative background plates (the hero's rotating backdrop photos) rather than a
 *      product or record a shopper is looking for — with none configured they render
 *      nothing at all and let the section's own art show through, instead of surfacing
 *      an opaque "not yet taken" placeholder across the whole hero.
 *
 * Renders into a shadow root rather than appending the <img>/placeholder directly as a
 * light-DOM child. assets/sat-runtime.js's own patch() trims any DOM under an element
 * that its vdom doesn't account for ("trim trailing DOM the vdom no longer accounts
 * for") — and since <sat-photo> never has children in the source markup, the vdom's own
 * children list for it is always empty, so on the SECOND render of any page (any state
 * change, anywhere in the app — not just something touching this photo) that cleanup
 * step deleted the image this element had injected itself, and nothing ever put it back:
 * the src attribute value hadn't changed, so attributeChangedCallback never fired again
 * to re-render. A shadow root's content isn't part of the host's light-DOM childNodes at
 * all, so the vdom patcher can no longer see it to "clean up" — this is the actual fix
 * for the "photo shows once then vanishes" bug, not a JS error anywhere else.
 */
(function () {
  'use strict';

  var EMPTY_BG = '#E0D6BF';

  function photoFor(el) {
    var direct = el.getAttribute('src');
    if (direct) return direct;
    var id = el.getAttribute('slot-id');
    var map = window.SAT_PHOTOS;
    if (id && map && map[id]) return map[id];
    return null;
  }

  function render(el) {
    var src = photoFor(el);
    if (el.__satSrc === src) return;
    el.__satSrc = src;
    var root = el.__satRoot;
    while (root.firstChild) root.removeChild(root.firstChild);

    if (src) {
      var img = document.createElement('img');
      img.setAttribute('alt', el.getAttribute('placeholder') || '');
      img.setAttribute('loading', 'lazy');
      img.setAttribute('decoding', 'async');
      if (el.getAttribute('fit') === 'contain') {
        img.setAttribute('style', 'width:100%;height:100%;object-fit:contain;object-position:50% 50%;display:block;');
        if (el.hasAttribute('auto-ratio')) {
          // Default to a gentle portrait ratio before the image loads (most gear
          // photos in this catalog run portrait or square) so there's no visible
          // jump for the common case; corrected the instant the real photo loads.
          if (!el.style.aspectRatio) el.style.aspectRatio = '4/5';
          img.addEventListener('load', function () {
            if (!img.naturalWidth || !img.naturalHeight) return;
            var ratio = img.naturalWidth / img.naturalHeight;
            // Clamp so one unusually panoramic or unusually tall photo can't force
            // an absurd box — beyond this range contain's own letterboxing takes
            // back over, which is still crop-free, just not edge-to-edge.
            ratio = Math.max(0.5, Math.min(2, ratio));
            el.style.aspectRatio = ratio;
          });
        }
      } else {
        // object-position biased toward the top third rather than dead-center: most of
        // these are gear laid flat or hung (collar/top of a jacket, mouth of a pouch),
        // and a center crop on a tall or narrow frame tends to cut that off.
        img.setAttribute('style', 'width:100%;height:100%;object-fit:cover;object-position:50% 25%;display:block;');
      }
      // src goes on LAST, after loading="lazy". A detached <img> starts fetching the
      // moment it gets a src, so setting it first made every photo on the page
      // (hundreds on the Manifest) download and decode at once — enough to make
      // iOS kill the tab ("A problem repeatedly occurred").
      img.setAttribute('src', src);
      root.appendChild(img);
      return;
    }

    if (el.hasAttribute('atmosphere')) return; // stay empty — no ugly placeholder over decorative art

    var frame = document.createElement('span');
    frame.setAttribute('style',
      'position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;' +
      'justify-content:center;gap:7px;padding:10px;box-sizing:border-box;text-align:center;' +
      'background:' + EMPTY_BG + ';' +
      'background-image:repeating-linear-gradient(-45deg,rgba(107,102,86,.07) 0 6px,transparent 6px 12px);');

    var rule = document.createElement('span');
    rule.setAttribute('style',
      'width:28px;height:28px;border:1px solid rgba(107,102,86,.5);border-radius:50%;' +
      'display:flex;align-items:center;justify-content:center;flex:none;');
    var dot = document.createElement('span');
    dot.setAttribute('style', 'width:11px;height:11px;border:1px solid rgba(107,102,86,.5);border-radius:50%;display:block;');
    rule.appendChild(dot);

    var label = document.createElement('span');
    label.setAttribute('style',
      "font-family:'Special Elite',monospace;font-size:8.5px;letter-spacing:.16em;" +
      'color:#8A8272;line-height:1.5;text-transform:uppercase;');
    label.textContent = 'Photograph not yet taken';

    frame.appendChild(rule);
    frame.appendChild(label);
    root.appendChild(frame);
  }

  function SatPhoto() {
    return Reflect.construct(HTMLElement, [], SatPhoto);
  }
  SatPhoto.prototype = Object.create(HTMLElement.prototype);
  SatPhoto.prototype.constructor = SatPhoto;
  Object.setPrototypeOf(SatPhoto, HTMLElement);

  SatPhoto.observedAttributes = ['src', 'slot-id'];
  SatPhoto.prototype.connectedCallback = function () {
    var s = this.getAttribute('style') || '';
    if (s.indexOf('position:') === -1) this.setAttribute('style', s + ';position:relative;');
    this.style.display = 'block';
    this.style.overflow = 'hidden';
    // Content lives in a shadow root, out of light-DOM reach of the vdom patcher
    // (see the file header comment) — fall back to the element itself on the
    // rare browser with no attachShadow, same behavior as before this fix.
    if (!this.__satRoot) this.__satRoot = this.attachShadow ? this.attachShadow({ mode: 'open' }) : this;
    render(this);
  };
  SatPhoto.prototype.attributeChangedCallback = function () {
    if (this.isConnected) render(this);
  };

  if (window.customElements && !window.customElements.get('sat-photo')) {
    window.customElements.define('sat-photo', SatPhoto);
  }
})();
